import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Eye, ArrowRightLeft, Clock, MessageSquare, Bot, User, XCircle, Hourglass, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/Toast';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, Grid, Card, Badge, Button,
  StatCard, StatValue, StatLabel, ModalOverlay, ModalBox, ModalHeader,
  ModalTitle, ModalBody, Flex, Divider, EmptyState, EmptyIcon, EmptyText
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';

const ConversationMonitor = () => {
  const { t } = useTranslation('monitor');
  const toast = useToast();
  const { tenantId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [agents, setAgents] = useState([]);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  const homeItem = isSallusAdmin
    ? { label: 'Libben Agents', path: '/sallus/dashboard' }
    : user.role === 'manager'
      ? { label: t('home', { ns: 'common' }), path: `/manager/${tenantId}` }
      : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` };

  const breadcrumbItems = [homeItem, { label: t('title') }];

  const sentimentColor   = { positive: theme.colors.success, negative: theme.colors.danger, neutral: theme.colors.warning };
  const sentimentVariant = { positive: 'success', negative: 'danger', neutral: 'warning' };

  // Cor do indicador de tempo ocioso: só vira alerta quando a vez é NOSSA
  // (waitingFor === 'us' = cliente aguardando resposta). Quando aguardamos o
  // cliente (waitingFor === 'customer'), fica neutro — o tempo parado é esperado.
  const idleColor = (conv) => {
    if (conv.waitingFor !== 'us') return theme.colors.textMuted; // aguardando cliente → neutro
    const s = conv.idleSeconds || 0;
    if (s > 15 * 60) return theme.colors.danger;   // > 15min → crítico
    if (s > 5 * 60)  return theme.colors.warning;  // 5–15min → atenção
    return theme.colors.success;                   // < 5min → ok
  };

  useEffect(() => {
    loadConversations();
    loadAgents();
    const interval = setInterval(loadConversations, 5000);
    return () => clearInterval(interval);
  }, [tenantId, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConversations = async () => {
    try {
      const res = await api.get(`/conversations/${tenantId}/active`);
      const all = res.data.conversations || [];
      const filtered = filter === 'all' ? all
        : filter === 'ai' ? all.filter(c => c.agent.type === 'ai')
        : all.filter(c => c.agent.type === 'human');
      setConversations(filtered);
    } catch (e) { console.error(e); }
  };

  const loadAgents = async () => {
    try {
      const [marketRes, teamRes] = await Promise.all([
        api.get(`/marketplace/tenants/${tenantId}/agents`),
        api.get(`/teams/${tenantId}/members`)
      ]);
      const aiAgents    = (marketRes.data.agents  || []).map(a => ({ id: a.instanceId || a.agentId, name: `${a.instanceName || a.name} (IA)`, type: 'ai',    available: true }));
      const humanAgents = (teamRes.data.members   || []).map(m => ({ id: m.memberId, name: m.name,           type: 'human', available: m.status !== 'offline' && m.status !== 'inactive' }));
      setAgents([...aiAgents, ...humanAgents]);
    } catch (e) { console.error(e); }
  };

  const handleTransfer = async (targetAgent) => {
    try {
      await api.post(`/conversations/${tenantId}/transfer`, {
        sessionId: selectedConv.id, targetAgentId: targetAgent.id, targetAgentType: targetAgent.type
      });
      setShowTransferModal(false); setSelectedConv(null); loadConversations();
    } catch (e) {
      toast.error(t('transferError', { msg: e.response?.data?.message || e.message }));
    }
  };

  const handleResolve = async () => {
    if (!selectedConv) return;
    setResolving(true);
    try {
      await api.post(`/conversations/${tenantId}/resolve`, { sessionId: selectedConv.id });
      toast.success(t('resolveSuccess'));
      setShowResolveModal(false); setSelectedConv(null); loadConversations();
    } catch (e) {
      toast.error(t('resolveError', { msg: e.response?.data?.message || e.message }));
    } finally {
      setResolving(false);
    }
  };

  const aiCount    = conversations.filter(c => c.agent.type === 'ai').length;
  const humanCount = conversations.filter(c => c.agent.type === 'human').length;
  const queueCount = conversations.filter(c => c.inQueue).length;

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <StatsRow>
            <StatCard accent={theme.colors.primary}><StatValue>{conversations.length}</StatValue><StatLabel>{t('activeConversations')}</StatLabel></StatCard>
            <StatCard accent={theme.colors.info}><StatValue>{aiCount}</StatValue><StatLabel>{t('withAI')}</StatLabel></StatCard>
            <StatCard accent={theme.colors.warning}><StatValue>{humanCount}</StatValue><StatLabel>{t('withHumans')}</StatLabel></StatCard>
            <StatCard accent={theme.colors.warning}><StatValue>{queueCount}</StatValue><StatLabel>{t('inQueueCount')}</StatLabel></StatCard>
          </StatsRow>
          <Flex gap={theme.spacing[2]}>
            {['all', 'ai', 'human'].map(f => (
              <Button key={f} variant={filter === f ? 'primary' : 'secondary'} size="sm" onClick={() => setFilter(f)}>
                {f === 'all' ? t('all') : f === 'ai' ? t('ai') : t('humans')}
              </Button>
            ))}
          </Flex>
        </PageHeader>

        {conversations.length === 0 ? (
          <EmptyState>
            <EmptyIcon><MessageSquare size={48} /></EmptyIcon>
            <EmptyText>{t('noConversations')}</EmptyText>
          </EmptyState>
        ) : (
          <Grid min="320px">
            {conversations.map(conv => (
              <ConvCard key={conv.id} accent={sentimentColor[conv.sentiment] || theme.colors.border}>
                <Flex justify="space-between" style={{ marginBottom: theme.spacing[3] }}>
                  <div>
                    <CustomerName>{conv.customer}</CustomerName>
                    <CustomerPhone>{conv.phone}</CustomerPhone>
                  </div>
                  <Badge variant={sentimentVariant[conv.sentiment] || 'neutral'}>
                    {t(`sentiment.${conv.sentiment}`) || conv.sentiment}
                  </Badge>
                </Flex>
                {conv.inQueue && (
                  <QueueBanner>
                    <Hourglass size={14} />
                    <span>{t('inQueue')}</span>
                    {conv.humanRequestedGroupName && (
                      <QueueGroup><Users size={12} /> {conv.humanRequestedGroupName}</QueueGroup>
                    )}
                  </QueueBanner>
                )}
                {(conv.reason || conv.summary) && (
                  <HandoffBox>
                    {conv.reason && (
                      <HandoffReasonText>
                        <strong>{t('handoffReason', { defaultValue: 'Motivo' })}:</strong> {conv.reason}
                      </HandoffReasonText>
                    )}
                    {conv.summary && <HandoffSummaryText>{conv.summary}</HandoffSummaryText>}
                  </HandoffBox>
                )}
                <AgentRow>
                  {conv.agent.type === 'ai' ? <Bot size={16} /> : <User size={16} />}
                  <AgentName>{conv.agent.name}</AgentName>
                  <Badge variant={conv.agent.type === 'ai' ? 'info' : 'warning'}>
                    {conv.agent.type === 'ai' ? t('ai') : t('humans')}
                  </Badge>
                </AgentRow>
                <ConvMessage>{conv.lastMessage}</ConvMessage>
                <Flex justify="space-between">
                  <Flex gap={theme.spacing[3]}>
                    <MetaItem
                      $color={idleColor(conv)}
                      title={conv.waitingFor === 'us'
                        ? t('idleWaitingUs', { defaultValue: 'Cliente aguardando resposta há {{t}}', t: conv.idleTime || conv.duration })
                        : t('idleWaitingCustomer', { defaultValue: 'Aguardando o cliente ({{t}})', t: conv.idleTime || conv.duration })}
                    >
                      <Clock size={12} /> {conv.idleTime || conv.duration}
                    </MetaItem>
                    <MetaItem><MessageSquare size={12} /> {conv.messageCount}</MetaItem>
                  </Flex>
                  <Flex gap={theme.spacing[2]}>
                    <Button size="sm" variant="secondary" onClick={() => { setSelectedConv(conv); setShowChatModal(true); }}><Eye size={14} /></Button>
                    <Button size="sm" variant="primary"   onClick={() => { setSelectedConv(conv); setShowTransferModal(true); }}><ArrowRightLeft size={14} /></Button>
                    <Button size="sm" variant="danger"    onClick={() => { setSelectedConv(conv); setShowResolveModal(true); }} title={t('resolve')}><XCircle size={14} /></Button>
                  </Flex>
                </Flex>
              </ConvCard>
            ))}
          </Grid>
        )}

        {showChatModal && selectedConv && (
          <ModalOverlay onClick={() => setShowChatModal(false)}>
            <ModalBox maxWidth="680px" onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{selectedConv.customer}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowChatModal(false)}>✕</Button>
              </ModalHeader>
              <ChatInfoBar>
                <span><strong>{t('phone')}</strong> {selectedConv.phone}</span>
                <span><strong>{t('agent')}</strong> {selectedConv.agent.name}</span>
                <span><strong>{t('duration')}</strong> {selectedConv.duration}</span>
              </ChatInfoBar>
              <ChatMessages>
                {(selectedConv.messages || []).map((msg, idx) => (
                  <ChatMsg key={idx} isAgent={msg.from === 'agent'}>
                    <Bubble isAgent={msg.from === 'agent'}>{msg.text}</Bubble>
                    <MsgTime>{msg.time ? new Date(msg.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</MsgTime>
                  </ChatMsg>
                ))}
              </ChatMessages>
            </ModalBox>
          </ModalOverlay>
        )}

        {showTransferModal && selectedConv && (
          <ModalOverlay onClick={() => setShowTransferModal(false)}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{t('transferTitle')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowTransferModal(false)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <Flex gap={theme.spacing[4]}>
                  <InfoPair><InfoLabel>{t('client')}</InfoLabel><InfoVal>{selectedConv.customer}</InfoVal></InfoPair>
                  <InfoPair><InfoLabel>{t('currentAgent')}</InfoLabel><InfoVal>{selectedConv.agent.name}</InfoVal></InfoPair>
                </Flex>
                <Divider />
                <SectionLabel>{t('selectDestination')}</SectionLabel>
                {agents.filter(a => a.id !== selectedConv.agent.id).map(agent => (
                  <AgentOption key={agent.id} disabled={!agent.available} onClick={() => agent.available && handleTransfer(agent)}>
                    {agent.type === 'ai' ? <Bot size={20} /> : <User size={20} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: theme.typography.sm }}>{agent.name}</div>
                      <AvailText available={agent.available}>{agent.available ? t('available') : t('busy')}</AvailText>
                    </div>
                    <Badge variant={agent.available ? 'success' : 'danger'}>{agent.available ? t('free') : t('busy')}</Badge>
                  </AgentOption>
                ))}
              </ModalBody>
            </ModalBox>
          </ModalOverlay>
        )}

        {showResolveModal && selectedConv && (
          <ModalOverlay onClick={() => !resolving && setShowResolveModal(false)}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{t('resolveTitle')}</ModalTitle>
                <Button variant="ghost" size="sm" disabled={resolving} onClick={() => setShowResolveModal(false)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <ResolveText>{t('resolveConfirm', { customer: selectedConv.customer })}</ResolveText>
                <Flex gap={theme.spacing[4]} style={{ marginTop: theme.spacing[3] }}>
                  <InfoPair><InfoLabel>{t('client')}</InfoLabel><InfoVal>{selectedConv.customer}</InfoVal></InfoPair>
                  <InfoPair><InfoLabel>{t('currentAgent')}</InfoLabel><InfoVal>{selectedConv.agent.name}</InfoVal></InfoPair>
                </Flex>
                <Divider />
                <Flex justify="flex-end" gap={theme.spacing[2]}>
                  <Button variant="secondary" disabled={resolving} onClick={() => setShowResolveModal(false)}>{t('cancel')}</Button>
                  <Button variant="danger" disabled={resolving} onClick={handleResolve}>
                    {resolving ? t('resolving') : t('confirmResolve')}
                  </Button>
                </Flex>
              </ModalBody>
            </ModalBox>
          </ModalOverlay>
        )}
      </PageContainer>
    </>
  );
};

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const StatsRow = styled.div`display: grid; grid-template-columns: repeat(4, 1fr); gap: ${theme.spacing[4]}; flex: 1;`;
const ConvCard = styled(Card)`border-left: 3px solid ${({ accent }) => accent}; display: flex; flex-direction: column; gap: ${theme.spacing[3]};`;
const CustomerName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const CustomerPhone = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const AgentRow = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[2]} ${theme.spacing[3]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.md}; color: ${theme.colors.textSecondary};`;
const AgentName = styled.span`flex: 1; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const ConvMessage = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary}; line-height: 1.5; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;`;
const MetaItem = styled.div`display: flex; align-items: center; gap: 4px; font-size: ${theme.typography.xs}; color: ${p => p.$color || theme.colors.textMuted}; font-weight: ${p => p.$color && p.$color !== theme.colors.textMuted ? theme.typography.semibold : 'inherit'};`;
const ChatInfoBar = styled.div`display: flex; gap: ${theme.spacing[6]}; padding: ${theme.spacing[3]} ${theme.spacing[6]}; background: ${theme.colors.gray50}; border-bottom: 1px solid ${theme.colors.border}; font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; flex-wrap: wrap;`;
const ChatMessages = styled.div`flex: 1; padding: ${theme.spacing[6]}; overflow-y: auto; display: flex; flex-direction: column; gap: ${theme.spacing[4]}; max-height: 60vh;`;
const ChatMsg = styled.div`display: flex; flex-direction: column; align-items: ${p => p.isAgent ? 'flex-end' : 'flex-start'};`;
const Bubble = styled.div`max-width: 70%; padding: ${theme.spacing[3]} ${theme.spacing[4]}; border-radius: ${theme.radii.xl}; background: ${p => p.isAgent ? theme.colors.primary : theme.colors.gray100}; color: ${p => p.isAgent ? theme.colors.white : theme.colors.textPrimary}; font-size: ${theme.typography.sm}; line-height: 1.5;`;
const MsgTime = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 4px;`;
const InfoPair = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const InfoLabel = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; font-weight: ${theme.typography.medium};`;
const InfoVal = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textPrimary};`;
const SectionLabel = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[2]};`;
const AgentOption = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; padding: ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.lg}; margin-bottom: ${theme.spacing[2]}; cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'}; opacity: ${p => p.disabled ? 0.5 : 1}; transition: ${theme.transitions.fast}; color: ${theme.colors.textSecondary}; &:hover { background: ${p => p.disabled ? 'transparent' : theme.colors.gray50}; border-color: ${p => p.disabled ? theme.colors.border : theme.colors.primary}; }`;
const AvailText = styled.div`font-size: ${theme.typography.xs}; color: ${p => p.available ? theme.colors.success : theme.colors.danger};`;
const ResolveText = styled.p`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; line-height: 1.5; margin: 0;`;
const QueueBanner = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[2]} ${theme.spacing[3]}; background: ${theme.colors.warning}22; border: 1px solid ${theme.colors.warning}; border-radius: ${theme.radii.md}; color: ${theme.colors.warning}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};`;
const QueueGroup = styled.span`display: inline-flex; align-items: center; gap: 4px; margin-left: auto; padding: 2px ${theme.spacing[2]}; background: ${theme.colors.warning}; color: ${theme.colors.white}; border-radius: ${theme.radii.sm}; font-weight: ${theme.typography.medium};`;
const HandoffBox = styled.div`margin-top: ${theme.spacing[2]}; padding: ${theme.spacing[2]} ${theme.spacing[3]}; background: ${theme.colors.infoLight}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};`;
const HandoffReasonText = styled.div`color: ${theme.colors.textPrimary};`;
const HandoffSummaryText = styled.div`margin-top: 2px; line-height: 1.5; white-space: pre-wrap;`;

export default ConversationMonitor;
